import type { HTMLAttributeAnchorTarget } from 'react';

export function isExternalLink(href: string): boolean {
  const clientDetectedUrl = typeof window !== 'undefined' ? window.location.origin : null;
  const serverDetectedUrl = typeof process !== 'undefined' ? process.env.BASE_URL : null;
  const baseUrl = clientDetectedUrl || serverDetectedUrl || '_external';

  // If it's not an absolute URL, it's internal
  if (!/^(https?:)?\/\//.test(href)) {
    return false;
  }

  // If we have a base URL, check if href belongs to the same origin
  if (baseUrl && baseUrl !== '_external') {
    try {
      const baseUrlObj = new URL(baseUrl);
      const hrefUrlObj = new URL(href);
      // Compare origins (protocol + hostname + port)
      return baseUrlObj.origin !== hrefUrlObj.origin;
    } catch {
      // If URL parsing fails, fall back to string comparison
      const normalizedBase = baseUrl.replace(/\/$/, '');
      const normalizedHref = href.replace(/\/$/, '');
      return !normalizedHref.startsWith(normalizedBase);
    }
  }

  // No base URL available, treat absolute URLs as external
  return true;
}

/**
 * Props returned by getExternalLinkProps for anchor elements
 */
export interface ExternalLinkProps {
  target: HTMLAttributeAnchorTarget | undefined;
  rel: string | undefined;
  isExternal: boolean;
}

/**
 * Returns the appropriate target and rel attributes for a link based on whether it's external.
 * External links get target="_blank" and rel="noopener noreferrer" for security.
 *
 * @param href - The URL to check
 * @param explicitTarget - Optional explicit target override (if '_blank', treats as external)
 * @returns Object with target, rel, and isExternal boolean
 *
 * @example
 * const { target, rel, isExternal } = getExternalLinkProps('https://example.com');
 * // Returns: { target: '_blank', rel: 'noopener noreferrer', isExternal: true }
 *
 * @example
 * const { target, rel } = getExternalLinkProps('/internal-page');
 * // Returns: { target: undefined, rel: undefined, isExternal: false }
 */
export function getExternalLinkProps(href: string, explicitTarget?: HTMLAttributeAnchorTarget): ExternalLinkProps {
  const isExternal = explicitTarget === '_blank' || isExternalLink(href);
  const shouldOpenInNewTab = explicitTarget === '_blank' || (isExternal && !explicitTarget);

  return {
    target: explicitTarget || (shouldOpenInNewTab ? '_blank' : undefined),
    rel: shouldOpenInNewTab ? 'noopener noreferrer' : undefined,
    isExternal,
  };
}
